import {
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const MEMORY_SIZE = 0x2000
const CHANNEL_START = 0x0010
const CHANNEL_SIZE = 16
const NAME_CHARSET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ?+-'

type WouxunClassicSpec = {
  id: string
  model: string
  summary: string
  channelCount: number
  nameStart: number
  nameStride: number
  queryModels: readonly string[]
  splitFlag: boolean
}

export type WouxunClassicProfile = {
  protocol: 'wouxun-classic'
  id: string
  vendor: 'Wouxun'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 6
  memorySize: number
  nameStart: number
  nameStride: number
  queryModels: readonly string[]
  splitFlag: boolean
  powerLevels: readonly ['High', 'Low']
}

const SPECS: readonly WouxunClassicSpec[] = [
  {
    id: 'wouxun-kg-uvd1p',
    model: 'KG-UVD1P',
    summary: 'Wouxun KG-UVD1P dual-band handheld.',
    channelCount: 128,
    nameStart: 0x1008,
    nameStride: 10,
    queryModels: ['HiWOUXUN', 'PROGUV6X'],
    splitFlag: true,
  },
  {
    id: 'wouxun-kg-uv6',
    model: 'KG-UV6',
    summary: 'Wouxun KG-UV6 dual-band handheld.',
    channelCount: 199,
    nameStart: 0x1010,
    nameStride: 16,
    queryModels: ['HiWXUVD1', 'HiKGUVD1'],
    splitFlag: true,
  },
  {
    id: 'wouxun-kg-816',
    model: 'KG-816',
    summary: 'Wouxun KG-816 dual-band handheld.',
    channelCount: 199,
    nameStart: 0x1010,
    nameStride: 16,
    queryModels: ['HiWOUXUN'],
    splitFlag: true,
  },
  {
    id: 'wouxun-kg-818',
    model: 'KG-818',
    summary: 'Wouxun KG-818 receiver with 199 channel memories.',
    channelCount: 199,
    nameStart: 0x1010,
    nameStride: 16,
    queryModels: ['HiWOUXUN'],
    splitFlag: true,
  },
  {
    id: 'wouxun-kg-805g',
    model: 'KG-805G',
    summary: 'Wouxun KG-805G GMRS handheld.',
    channelCount: 128,
    nameStart: 0x1010,
    nameStride: 16,
    queryModels: ['HiWOUXUN'],
    splitFlag: false,
  },
]

export const WOUXUN_CLASSIC_PROFILES: readonly WouxunClassicProfile[] =
  SPECS.map((spec) => ({
    protocol: 'wouxun-classic',
    id: spec.id,
    vendor: 'Wouxun',
    model: spec.model,
    summary: spec.summary,
    tags: [
      'Experimental',
      'Read + write',
      `${spec.channelCount} channels`,
      '6-character names',
    ],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: spec.channelCount,
    nameLength: 6,
    memorySize: MEMORY_SIZE,
    nameStart: spec.nameStart,
    nameStride: spec.nameStride,
    queryModels: spec.queryModels,
    splitFlag: spec.splitFlag,
    powerLevels: ['High', 'Low'],
  }))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'High',
    skip: false,
  }
}

function readUint16LE(raw: Uint8Array, offset: number): number {
  return raw[offset] | (raw[offset + 1] << 8)
}

function writeUint16LE(raw: Uint8Array, offset: number, value: number): void {
  raw[offset] = value & 0xff
  raw[offset + 1] = (value >> 8) & 0xff
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(value: number): DecodedTone {
  if (value === 0 || value === 0xffff) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value & 0x2000) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: Number((value & 0x01ff).toString(8)),
      polarity: value & 0x8000 ? 'R' : 'N',
    }
  }
  return {
    mode: 'ctcss',
    ctcss: value / 10,
    dtcs: null,
    polarity: 'N',
  }
}

function encodeTone(channel: WebChannel): number {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    return Math.round(channel.ctcss * 10)
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    let value = (Number.parseInt(channel.dtcs.toString(), 8) & 0x01ff) | 0x2800
    if (channel.dtcsPolarity === 'R') value |= 0x8000
    return value
  }
  return 0xffff
}

function decodeName(image: Uint8Array, profile: WouxunClassicProfile, number: number): string {
  const start = profile.nameStart + (number - 1) * profile.nameStride
  let name = ''
  for (let index = 0; index < 6; index += 1) {
    const value = image[start + index]
    if (value === 0xff || value >= NAME_CHARSET.length) break
    name += NAME_CHARSET[value]
  }
  return name
}

export function parseWouxunClassicChannel(
  image: Uint8Array,
  profile: WouxunClassicProfile,
  number: number,
): WebChannel {
  const start = CHANNEL_START + (number - 1) * CHANNEL_SIZE
  const raw = image.slice(start, start + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || raw.every((value) => value === 0xff)) {
    return emptyChannel(number)
  }
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number)
  const txAll = raw.slice(4, 8).every((value) => value === 0xff)
  const txFrequency = txAll ? null : decodeLbcd(raw.slice(4, 8)) ?? rxFrequency
  const txTone = decodeTone(readUint16LE(raw, 10))
  const rxTone = decodeTone(readUint16LE(raw, 8))
  const selected = txTone.mode === 'none' ? rxTone : txTone
  return {
    number,
    empty: false,
    name: decodeName(image, profile, number),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: raw[13] & 0x10 ? 'FM' : 'NFM',
    power: raw[13] & 0x20 ? 'High' : 'Low',
    skip: (raw[13] & 0x40) === 0,
  }
}

export function parseWouxunClassicChannels(
  image: Uint8Array,
  profile: WouxunClassicProfile,
): WebChannel[] {
  return Array.from({ length: profile.channelMaximum }, (_, index) =>
    parseWouxunClassicChannel(image, profile, index + 1),
  )
}

export function applyWouxunClassicChannel(
  image: Uint8Array,
  profile: WouxunClassicProfile,
  channel: WebChannel,
): void {
  const start = CHANNEL_START + (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(start, start + CHANNEL_SIZE)
  const nameStart = profile.nameStart + (channel.number - 1) * profile.nameStride
  image.fill(0xff, nameStart, nameStart + 6)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (raw.every((value) => value === 0xff)) raw.fill(0)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  const tone = encodeTone(channel)
  writeUint16LE(raw, 8, tone)
  writeUint16LE(raw, 10, tone)
  const split = channel.txFrequency && channel.txFrequency !== channel.rxFrequency
  raw[13] =
    (profile.splitFlag && split ? 0x80 : raw[13] & 0x80) |
    (channel.skip ? 0 : 0x40) |
    (channel.power === 'High' ? 0x20 : 0) |
    (channel.mode === 'FM' ? 0x10 : 0) |
    (raw[13] & 0x0f)
  for (let index = 0; index < Math.min(6, channel.name.length); index += 1) {
    const code = NAME_CHARSET.indexOf(channel.name[index].toUpperCase())
    image[nameStart + index] = code >= 0 ? code : NAME_CHARSET.indexOf('?')
  }
}

async function readAck(transport: RadioTransport): Promise<void> {
  if ((await transport.readExact(1, 2500))[0] !== ACK) {
    throw new Error('The Wouxun radio rejected the operation.')
  }
}

async function enter(
  profile: WouxunClassicProfile,
  transport: RadioTransport,
): Promise<void> {
  await transport.discardInput()
  let identified = false
  for (let attempt = 0; attempt < 10 && !identified; attempt += 1) {
    const query = profile.queryModels[attempt % profile.queryModels.length]
    await transport.write(
      Uint8Array.from([...new TextEncoder().encode(query), 0x02]),
    )
    try {
      const response = await transport.readExact(9, 1800)
      const model = new TextDecoder().decode(response.slice(2, 8))
      if (model !== 'KG669V') {
        throw new Error(`Unexpected Wouxun identity ${model}.`)
      }
      identified = true
    } catch {
      await transport.discardInput()
    }
  }
  if (!identified) throw new Error(`The radio did not identify as ${profile.model}.`)
  await transport.write(Uint8Array.of(0x02, ACK))
  await readAck(transport)
}

export async function readWouxunClassicRadioWithTransport(
  profile: WouxunClassicProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enter(profile, transport)
  const image = new Uint8Array(MEMORY_SIZE)
  const blockSize = 0x40
  const total = MEMORY_SIZE / blockSize
  for (let index = 0; index < total; index += 1) {
    const address = index * blockSize
    await transport.write(
      Uint8Array.of(0x52, (address >> 8) & 0xff, address & 0xff, blockSize),
    )
    const response = await transport.readExact(4 + blockSize, 3500)
    image.set(response.slice(4), address)
    await transport.write(Uint8Array.of(ACK))
    await readAck(transport)
    onProgress?.({
      current: index + 1,
      total,
      label: 'Reading Wouxun memory',
    })
  }
  return { image, reportedModel: profile.model }
}

export async function writeWouxunClassicRadioWithTransport(
  profile: WouxunClassicProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than Wouxun memory.')
  }
  await enter(profile, transport)
  const blockSize = 0x10
  const total = MEMORY_SIZE / blockSize
  for (let index = 0; index < total; index += 1) {
    const address = index * blockSize
    const packet = new Uint8Array(4 + blockSize)
    packet.set([
      0x57,
      (address >> 8) & 0xff,
      address & 0xff,
      blockSize,
    ])
    packet.set(image.slice(address, address + blockSize), 4)
    await transport.write(packet)
    await readAck(transport)
    onProgress?.({
      current: index + 1,
      total,
      label: 'Writing Wouxun memory',
    })
  }
}
